import { Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';
import { ValueBadge } from './ValueBadge';

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';
export type SaveStatusTone = 'neutral' | 'success' | 'warning' | 'danger';
export type SaveStatusDescriptor = ({ state: SaveState; label?: string } | { state?: never; label: string }) & {
  detail?: ReactNode;
  busy?: boolean;
  tone?: SaveStatusTone;
  action?: { label: string; onSelect: () => void; disabled?: boolean };
};
export type SaveStatusProps = { status: SaveStatusDescriptor };

const presentation: Record<SaveState, { label: string; tone: SaveStatusTone }> = {
  idle: { label: '변경 없음', tone: 'neutral' },
  pending: { label: '저장 대기 중', tone: 'warning' },
  saving: { label: '저장 중…', tone: 'neutral' },
  saved: { label: '저장됨', tone: 'success' },
  error: { label: '저장 실패', tone: 'danger' },
  conflict: { label: '저장 충돌', tone: 'danger' },
};

/** 변경 여부·저장·버전 충돌은 호출자가 판정하고 상태와 복구 행동을 전달합니다. */
export function SaveStatus({ status }: SaveStatusProps) {
  const defaults = presentation[status.state ?? 'idle'];
  const tone = status.tone ?? defaults.tone;
  const busy = status.busy ?? status.state === 'saving';
  const badge = <ValueBadge tone={tone} role="status" aria-live="polite" aria-atomic="true" label={
    <Stack component="span" direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center' }}>
      {busy ? <CircularProgress aria-hidden size={designTokens.layout.valueBadgeIconSize} color="inherit" /> : null}
      <span>{status.label ?? defaults.label}</span>
    </Stack>
  } />;
  return <Stack direction="row" spacing={1} useFlexGap sx={{ minWidth: 0, flexWrap: 'wrap', alignItems: 'center' }}>
    {status.detail ? <Tooltip describeChild title={status.detail}>
      <Box tabIndex={0} sx={{ borderRadius: designTokens.layout.valueBadgeRadius, '&:focus-visible': createFocusRingStyles({ inset: true }) }}>{badge}</Box>
    </Tooltip> : badge}
    {status.action ? <Button size="small" color={tone === 'danger' ? 'error' : 'primary'} disabled={busy || status.action.disabled} onClick={status.action.onSelect} sx={{ minWidth: 0 }}>{status.action.label}</Button> : null}
  </Stack>;
}
